import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  FileText,
  Check,
  ArrowRight,
  Trash2,
  Send,
  CircleOff,
  Copy,
} from "lucide-react";
import {
  Quote,
  QuoteVersion,
  useCreateQuote,
  useDeleteQuote,
  useCreateQuoteVersion,
  useCopyQuoteFromProject,
  getNextVersionNumber,
  useProjectQuotes,
} from "@/hooks/useQuotes";
import {
  useQuoteVersionGroupsWithItems,
  useDuplicateVersionContent,
  calculateQuoteGroupsTotals,
} from "@/hooks/useQuoteEntities";
import { formatCurrency } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CopyFromProjectSelector } from "@/components/project-detail/CopyFromProjectSelector";
import type { CopyDateAnchors } from "@/lib/copy-from-project";
import { toast } from "sonner";

function QuoteTotalCell({ version }: { version: QuoteVersion }) {
  const { data: groups = [] } = useQuoteVersionGroupsWithItems(version.id);
  const totals = calculateQuoteGroupsTotals(
    groups,
    version.project_discount_percent,
    version.vat_rate as "CH_8_1" | "NONE",
  );
  return <>{formatCurrency(totals.grossTotal)}</>;
}

interface QuotesListCardProps {
  projectId: string;
  projectName: string;
  previousEditionProjectId: string | null;
  projectDates: CopyDateAnchors;
  quotes: Quote[];
  isLoading: boolean;
  onSelectQuote: (quoteId: string) => void;
  onSelectVersion: (quoteId: string, versionId: string) => void;
}

const VERSION_STATUS_BADGES = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
  sent: { label: "Sent", className: "bg-blue-600 text-white" },
  accepted: { label: "Accepted", className: "bg-green-600 text-white" },
  rejected: { label: "Rejected", className: "bg-red-600 text-white" },
} as const;

function QuoteVersionStatusBadge({ version }: { version: QuoteVersion }) {
  const status = version.status || (version.is_accepted ? "accepted" : "draft");
  const config = VERSION_STATUS_BADGES[status];

  return (
    <Badge variant="secondary" className={config.className}>
      {status === "accepted" ? <Check className="h-3 w-3 mr-1" /> : null}
      {status === "sent" ? <Send className="h-3 w-3 mr-1" /> : null}
      {status === "rejected" ? <CircleOff className="h-3 w-3 mr-1" /> : null}
      {config.label}
    </Badge>
  );
}

export function QuotesListCard({
  projectId,
  projectName,
  previousEditionProjectId,
  projectDates: _projectDates,
  quotes,
  isLoading,
  onSelectQuote,
  onSelectVersion,
}: QuotesListCardProps) {
  const [newQuoteName, setNewQuoteName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [sourceProjectId, setSourceProjectId] = useState<string | null>(
    previousEditionProjectId,
  );
  const [sourceQuoteId, setSourceQuoteId] = useState("");
  const [sourceVersionId, setSourceVersionId] = useState("");
  const [deleteQuoteId, setDeleteQuoteId] = useState<string | null>(null);

  const createQuote = useCreateQuote();
  const createVersion = useCreateQuoteVersion();
  const copyQuote = useCopyQuoteFromProject();
  const deleteQuote = useDeleteQuote();
  const duplicateContent = useDuplicateVersionContent();
  const { data: sourceQuotes = [], isLoading: sourceQuotesLoading } =
    useProjectQuotes(sourceProjectId ?? undefined);

  const handleCreateQuote = () => {
    if (!newQuoteName.trim()) return;

    createQuote.mutate(
      { projectId, name: newQuoteName.trim() },
      {
        onSuccess: (quote) => {
          createVersion.mutate(
            { quoteId: quote.id, versionNumber: 1 },
            {
              onSuccess: (version) => {
                setNewQuoteName("");
                setIsDialogOpen(false);
                onSelectVersion(quote.id, version.id);
              },
            },
          );
        },
      },
    );
  };

  const handleDeleteQuote = () => {
    if (!deleteQuoteId) return;
    deleteQuote.mutate(deleteQuoteId, {
      onSuccess: () => setDeleteQuoteId(null),
    });
  };

  const getQuoteStatus = (quote: Quote) => {
    const hasAccepted = quote.versions?.some(
      (v) =>
        (v.status || (v.is_accepted ? "accepted" : "draft")) === "accepted",
    );
    return hasAccepted;
  };

  const getLatestVersion = (quote: Quote): QuoteVersion | null => {
    const visibleVersions =
      quote.versions?.filter((version) => version.status !== "rejected") || [];
    if (visibleVersions.length === 0) return null;
    return [...visibleVersions].sort(
      (a, b) => b.version_number - a.version_number,
    )[0];
  };

  const handleCreateNewVersion = async (quote: Quote) => {
    const latestVersion = getLatestVersion(quote);
    const nextNum = getNextVersionNumber(quote);

    createVersion.mutate(
      {
        quoteId: quote.id,
        versionNumber: nextNum,
        projectDiscountPercent: latestVersion?.project_discount_percent,
        vatRate: latestVersion?.vat_rate as "CH_8_1" | "NONE" | undefined,
      },
      {
        onSuccess: (data) => {
          if (latestVersion) {
            duplicateContent.mutate({
              sourceVersionId: latestVersion.id,
              targetVersionId: data.id,
            });
          }
          onSelectVersion(quote.id, data.id);
        },
      },
    );
  };

  const getPreferredVersion = (quote: Quote | undefined) => {
    if (!quote) return null;
    const versions = quote.versions ?? [];
    return (
      versions.find(
        (version) =>
          (version.status || (version.is_accepted ? "accepted" : "draft")) ===
          "accepted",
      ) ??
      [...versions]
        .filter((version) => version.status !== "rejected")
        .sort((a, b) => b.version_number - a.version_number)[0] ??
      null
    );
  };

  const selectedSourceQuote = sourceQuotes.find(
    (quote) => quote.id === sourceQuoteId,
  );
  const selectedSourceVersion =
    selectedSourceQuote?.versions?.find(
      (version) => version.id === sourceVersionId,
    ) ?? null;

  const handleOpenCopyDialog = () => {
    setSourceProjectId(previousEditionProjectId);
    setSourceQuoteId("");
    setSourceVersionId("");
    setCopyDialogOpen(true);
  };

  const handleSourceProjectChange = (nextProjectId: string | null) => {
    setSourceProjectId(nextProjectId);
    setSourceQuoteId("");
    setSourceVersionId("");
  };

  const handleSourceQuoteChange = (nextQuoteId: string) => {
    setSourceQuoteId(nextQuoteId);
    const nextQuote = sourceQuotes.find((quote) => quote.id === nextQuoteId);
    setSourceVersionId(getPreferredVersion(nextQuote)?.id ?? "");
  };

  const handleCopyQuote = async () => {
    if (!selectedSourceQuote || !selectedSourceVersion) {
      return;
    }

    try {
      const copied = await copyQuote.mutateAsync({
        targetProjectId: projectId,
        sourceQuoteId: selectedSourceQuote.id,
        sourceVersionId: selectedSourceVersion.id,
      });
      setCopyDialogOpen(false);
      onSelectVersion(copied.quoteId, copied.versionId);
    } catch (error) {
      toast.error(
        `Failed to copy quote: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  return (
    <>
      <Card data-testid="quotes-list-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Quotes
              </CardTitle>
              <CardDescription>Manage project quotations</CardDescription>
            </div>
            <Button
              offlineEdit
              variant="outline"
              size="sm"
              onClick={handleOpenCopyDialog}
              data-testid="copy-from-quote-button"
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy from
            </Button>
            <Dialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (open && quotes.length === 0) {
                  setNewQuoteName(projectName);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button offlineEdit size="sm" data-testid="new-quote-button">
                  <Plus className="h-4 w-4 mr-1" />
                  New Quote
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Quote</DialogTitle>
                  <DialogDescription>
                    Enter a name for the new quote
                  </DialogDescription>
                </DialogHeader>
                <Input
                  placeholder="Quote name"
                  value={newQuoteName}
                  onChange={(e) => setNewQuoteName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateQuote()}
                  data-testid="quote-name-input"
                />
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    offlineEdit
                    onClick={handleCreateQuote}
                    disabled={createQuote.isPending || createVersion.isPending}
                    data-testid="create-quote-submit"
                  >
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm">No quotes yet</p>
              <p className="text-xs mt-1">Create a quote to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {quotes.map((quote) => {
                const hasAccepted = getQuoteStatus(quote);
                const latestVersion = getLatestVersion(quote);
                const visibleVersions =
                  quote.versions
                    ?.filter((version) => version.status !== "rejected")
                    .sort((a, b) => b.version_number - a.version_number) || [];

                return (
                  <div
                    key={quote.id}
                    className="rounded-lg border bg-card"
                    data-testid={`quote-row-${quote.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 p-3 group hover:bg-accent/30">
                      <div
                        className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                        onClick={() => {
                          if (latestVersion) {
                            onSelectVersion(quote.id, latestVersion.id);
                          } else {
                            onSelectQuote(quote.id);
                          }
                        }}
                      >
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <p
                            className="font-medium truncate"
                            data-testid={`quote-row-title-${quote.id}`}
                          >
                            {quote.quote_number} – {quote.name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {hasAccepted ? (
                              <span className="flex items-center gap-1 text-green-600">
                                <Check className="h-3 w-3" />
                                Accepted
                              </span>
                            ) : (
                              <span>No accepted version</span>
                            )}
                            <span>
                              • {visibleVersions.length} active version(s)
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium tabular-nums">
                          {latestVersion ? (
                            <QuoteTotalCell version={latestVersion} />
                          ) : (
                            formatCurrency(0)
                          )}
                        </span>
                        <Button
                          offlineEdit
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateNewVersion(quote);
                          }}
                          title="New version (duplicate latest)"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button
                          offlineEdit
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteQuoteId(quote.id);
                          }}
                          title="Delete quote"
                          data-testid={`delete-quote-${quote.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            if (latestVersion) {
                              onSelectVersion(quote.id, latestVersion.id);
                            } else {
                              onSelectQuote(quote.id);
                            }
                          }}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {visibleVersions.length > 0 ? (
                      <div className="border-t">
                        {visibleVersions.map((version) => (
                          <button
                            key={version.id}
                            type="button"
                            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-accent/50"
                            onClick={() =>
                              onSelectVersion(quote.id, version.id)
                            }
                            data-testid={`quote-version-row-${version.id}`}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">
                                  V{version.version_number}
                                </span>
                                {version.internal_name ? (
                                  <span className="truncate text-sm text-muted-foreground">
                                    {version.internal_name}
                                  </span>
                                ) : null}
                                <QuoteVersionStatusBadge version={version} />
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm tabular-nums">
                                <QuoteTotalCell version={version} />
                              </span>
                              <ArrowRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!deleteQuoteId}
        onOpenChange={() => setDeleteQuoteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quote</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this quote? This will also delete
              all versions and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuote}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-quote"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Copy from
            </DialogTitle>
            <DialogDescription>
              Select a project, quote, and version to copy into this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Project</label>
              <CopyFromProjectSelector
                value={sourceProjectId}
                onChange={handleSourceProjectChange}
                currentProjectId={projectId}
                placeholder="Select source project"
                testId="copy-quote-source-project"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Quote</label>
              <Select
                value={sourceQuoteId}
                onValueChange={handleSourceQuoteChange}
                disabled={!sourceProjectId || sourceQuotesLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      sourceQuotesLoading ? "Loading quotes..." : "Select quote"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {sourceQuotes.map((quote) => (
                    <SelectItem key={quote.id} value={quote.id}>
                      {quote.quote_number} - {quote.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Version</label>
              <Select
                value={sourceVersionId}
                onValueChange={setSourceVersionId}
                disabled={!selectedSourceQuote}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select version" />
                </SelectTrigger>
                <SelectContent>
                  {(selectedSourceQuote?.versions ?? []).map((version) => (
                    <SelectItem key={version.id} value={version.id}>
                      V{version.version_number}
                      {version.internal_name
                        ? ` - ${version.internal_name}`
                        : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleCopyQuote}
              disabled={!selectedSourceVersion || copyQuote.isPending}
            >
              Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
